import { Link } from "react-router-dom";
import { Palette, Heart, Sparkles, Users, Target } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 font-medium text-sm mb-6">
              <Heart className="w-4 h-4 fill-violet-500" />
              Our Story
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              About ColorWonder AI
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We believe every child deserves magical, personalized coloring books that spark their imagination and creativity.
            </p>
          </div>

          {/* Mission Card */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Our Mission
              </h2>
            </div>
            <p className="text-slate-600 leading-relaxed text-lg">
              ColorWonder AI was born from a simple idea: making personalized coloring books accessible to everyone. 
              We combine cutting-edge AI technology with a love for creativity to help parents, teachers, and kids 
              create unique, printable coloring pages in seconds.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Creativity First
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                We believe in nurturing imagination. Every coloring page is designed to inspire creativity and joy.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Family Friendly
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                All our content is safe and appropriate for children of all ages. Quality time, worry-free.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Made with Love
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Every feature is crafted with care, keeping parents and children at the heart of our decisions.
              </p>
            </div>
          </div>

          {/* Story Section */}
          <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-3xl p-8 md:p-12 border border-violet-100">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Story Behind ColorWonder
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                ColorWonder AI started when we realized how difficult it was to find unique, themed coloring pages 
                for children. Generic coloring books often didn't capture the specific interests of kids - whether 
                it's their favorite animals, mythological characters, or imaginative worlds.
              </p>
              <p>
                We asked ourselves: "What if anyone could create a personalized coloring book in seconds?" 
                Using the latest AI technology, we built ColorWonder to turn that dream into reality.
              </p>
              <p>
                Today, thousands of families use ColorWonder to create magical coloring experiences. Whether it's 
                a rainy day activity, a birthday party theme, or educational content for classrooms - we're here 
                to help bring creativity to life.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-lg hover:shadow-xl transition-all"
            >
              <Sparkles className="w-5 h-5" />
              Start Creating Today
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
